import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";

export default function NotificationPopup({
  open,
  onClose,
  notifications = [],
  onMarkRead,
  onMarkAll,
  busyIds = [],
  busyAll = false,
  autoCloseMs = 10000,
  pauseOnHover = true,
  showTimeoutBar = true,
}) {
  const unread = useMemo(() => notifications.filter((n) => !n.is_read).length, [notifications]);

  // --- Auto-close + progress state
  const [progress, setProgress] = useState(1); // 1 -> full bar, 0 -> empty
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const remainingRef = useRef(autoCloseMs);
  const runningRef = useRef(false);

  const stop = useCallback(() => {
    runningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const tick = useCallback(
    (now) => {
      if (!runningRef.current) return;
      const elapsed = now - startRef.current;
      const left = Math.max(remainingRef.current - elapsed, 0);
      setProgress(left / autoCloseMs);

      if (left <= 0) {
        stop();
        Promise.resolve().then(() => onClose && onClose());
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [autoCloseMs, onClose, stop]
  );

  const start = useCallback(() => {
    if (!open) return;
    startRef.current = performance.now();
    runningRef.current = true;
    rafRef.current = requestAnimationFrame(tick);
  }, [open, tick]);

  const reset = useCallback(() => {
    stop();
    remainingRef.current = autoCloseMs;
    setProgress(1);
  }, [autoCloseMs, stop]);

  // Start/Reset on open changes
  useEffect(() => {
    if (!open) {
      stop();
      return;
    }
    reset();
    start();
    return stop;
  }, [open, reset, start, stop]);

  // Clean up on unmount
  useEffect(() => stop, [stop]);

  // Hover to pause/resume (attach to the panel container below)
  const handleMouseEnter = () => {
    if (!pauseOnHover || !runningRef.current) return;
    const pausedAt = performance.now();
    const elapsed = pausedAt - startRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
    stop();
  };
  const handleMouseLeave = () => {
    if (!pauseOnHover || !open || remainingRef.current <= 0) return;
    start();
  };

  // Let MUI call this for backdrop click and Escape
  const handleDialogClose = (event, reason) => {
    // Only close for backdrop or escape to mimic your old behavior
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      stop();
      onClose && onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      keepMounted
      // No other MUI components used—just Dialog.
      // Position bottom-right; add padding like your old p-4; tint backdrop to 40%.
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0,0,0,0.40)" },
        },
        root: {
          sx: {
            "& .MuiDialog-container": {
              alignItems: "flex-end",
              justifyContent: "flex-end",
              // padding: 16, // ~ p-4
            },
          },
        },
        paper: {
          // Keep MUI Paper but style it to look like your Tailwind panel
          sx: {
            margin: 0,
            borderRadius: 12, // rounded-xl
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", // shadow-2xl-ish
            border: "1px solid rgba(0,0,0,0.05)", // ring-1 ring-black/5
            width: "100%",
            maxWidth: 672, // max-w-xl
            overflow: "visible",
          },
        },
      }}
      // Prevent auto focus stealing if you prefer the old feel
      disableAutoFocus
      // Keep focus trap (accessibility) but no extra MUI tags in your JSX.
    >
      {/* Your original panel content, unchanged */}
      <div
        className="relative w-full rounded-xl bg-white"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Timeout bar */}
        {showTimeoutBar && (
          <div className="absolute left-0 top-0 h-1 w-full overflow-hidden rounded-t-xl bg-gray-200">
            <div
              className="h-full bg-blue-600"
              style={{
                transform: `scaleX(${Math.max(0, Math.min(1, progress))})`,
                transformOrigin: "left",
                transition: "transform 100ms linear",
              }}
              aria-hidden="true"
            />
          </div>
        )}

        <div className="flex items-center gap-3 border-b px-4 py-3 pt-4">
          <div className="flex items-center gap-2">
            <Bell />
            <h2 className="text-base font-semibold">Notifications</h2>
            {unread > 0 && (
              <span className="ml-1 rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* <button
              onClick={onMarkAll}
              disabled={!unread || busyAll}
              className="text-sm text-blue-600 hover:underline disabled:opacity-40"
            >
              {busyAll ? "Marking…" : "Mark all read"}
            </button> */}
            <button
              onClick={() => {
                stop();
                onClose && onClose();
              }}
              className="rounded-md border px-2.5 py-1.5 text-sm hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div className="max-h-96 space-y-3 overflow-y-auto p-4">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No notifications</div>
          ) : (
            notifications.map((n) => (
              <div key={n.id} className={`rounded-lg border p-3 ${n.is_read ? "border-gray-200" : "border-blue-400"}`}>
                <div className="flex items-start gap-3">
                  <Bell small />
                  <div className="flex-1 ">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold">{n.title}</div>
                        <div className="ml-auto shrink-0 text-xs text-gray-500">{ago(n.created_at)}</div>
                      </div>
                      <p className="mt-1 text-sm text-gray-700">{n.message}</p>
                    </div>
                    <div className="flex justify-between items-end">
                      {n.target_url && (
                        <a
                          href={n.target_url}
                          className=" inline-block text-sm text-blue-600 underline-offset-4 hover:underline"
                        >
                          View details →
                        </a>
                      )}
                      {!n.is_read && (
                        <div className=" flex justify-end">
                          <button
                            onClick={() => onMarkRead(n.id)}
                            disabled={busyIds.includes(n.id)}
                            className="rounded-md border bg-gray-50 px-3 py-1.5 text-sm hover:bg-gray-100 disabled:opacity-50"
                          >
                            ✓ Mark as read
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* (Optional) Accessible live region for countdown; hidden visually */}
        <div className="sr-only" aria-live="polite">
          Popup will close in {Math.ceil(progress * (autoCloseMs / 1000))} seconds
        </div>
      </div>
    </Dialog>
  );
}

const ago = (d) => {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function Bell({ small }) {
  return (
    <svg
      width={small ? 16 : 18}
      height={small ? 16 : 18}
      viewBox="0 0 24 24"
      fill="none"
      stroke={small ? "#2563eb" : "currentColor"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}
